import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { deleteAdminCheckoutPhoto, getAdminCheckout } from "../api/adminCheckouts";
import { BACKEND_URL } from "../api/api";

const imageBase = BACKEND_URL;
const formatDate = (value) => value ? new Date(value).toLocaleDateString("nl-BE") : "-";
const formatDateTime = (value) => value ? new Date(value).toLocaleString("nl-BE") : "-";

export default function AdminCheckoutDetail() {
  const { id } = useParams();
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const queryClient = useQueryClient();
  const { data: checkout, isLoading, isError } = useQuery({
    queryKey: ["admin", "checkout", id],
    queryFn: () => getAdminCheckout(id)
  });
  const deletePhotoMutation = useMutation({
    mutationFn: deleteAdminCheckoutPhoto,
    onSuccess: () => {
      toast.success("Foto verwijderd");
      queryClient.invalidateQueries({ queryKey: ["admin", "checkout", id] });
      queryClient.invalidateQueries({ queryKey: ["admin", "checkouts"] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Foto verwijderen mislukt");
    }
  });

  const removePhoto = (event, photoId) => {
    event.stopPropagation();
    if (window.confirm("Deze foto verwijderen?")) {
      deletePhotoMutation.mutate(photoId);
    }
  };

  if (isLoading) return <div className="loading-state">Check-out laden...</div>;

  if (isError || !checkout) {
    return (
      <section className="page">
        <div className="panel panel-pad">
          <h1 className="page-title">Check-out niet gevonden</h1>
          <Link className="button secondary" to="/admin/checkouts">Terug naar check-outs</Link>
        </div>
      </section>
    );
  }

  const reservation = checkout.reservation;
  const trailer = checkout.trailer;

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Check-out #{checkout.id}</h1>
          <p className="page-subtitle">Bevestigd op {formatDateTime(checkout.confirmedAt)}</p>
        </div>
        <Link className="button secondary" to="/admin/checkouts">Terug</Link>
      </header>

      <div className="trailer-detail-layout">
        <section className="panel panel-pad">
          <div className="detail-section">
            <h2 className="detail-title">Reservatie</h2>
            <div className="detail-grid">
              <div className="detail-item"><div className="detail-label">Reservatie</div><div className="detail-value">#{checkout.reservationId}</div></div>
              <div className="detail-item"><div className="detail-label">Periode</div><div className="detail-value">{formatDate(reservation?.startDate)} tot {formatDate(reservation?.endDate)}</div></div>
              <div className="detail-item"><div className="detail-label">Status</div><div className="detail-value">{reservation?.status || "-"}</div></div>
              <div className="detail-item"><div className="detail-label">Uitgecheckt</div><div className="detail-value">{formatDateTime(reservation?.checkOutAt)}</div></div>
            </div>
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Klant</h2>
            <div className="detail-grid">
              <div className="detail-item"><div className="detail-label">Naam</div><div className="detail-value">{reservation?.firstName || ""} {reservation?.name || "-"}</div></div>
              <div className="detail-item"><div className="detail-label">Telefoon</div><div className="detail-value">{reservation?.phone || "-"}</div></div>
              <div className="detail-item"><div className="detail-label">E-mail</div><div className="detail-value">{reservation?.email || "-"}</div></div>
            </div>
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Trailer</h2>
            <div className="detail-grid">
              <div className="detail-item"><div className="detail-label">Naam</div><div className="detail-value">{trailer?.name || "-"}</div></div>
              <div className="detail-item"><div className="detail-label">Status</div><div className="detail-value">{trailer?.status || "-"}</div></div>
              <div className="detail-item"><div className="detail-label">MTM</div><div className="detail-value">{trailer?.mtm || "-"} kg</div></div>
            </div>
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Notities</h2>
            <p className="page-subtitle">{checkout.notes || "Geen notities opgegeven."}</p>
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Handtekening</h2>
            {checkout.checkOutSignatureUrl ? (
              <img className="signature-image" src={`${imageBase}${checkout.checkOutSignatureUrl}`} alt="Check-out handtekening" />
            ) : (
              <div className="empty-state">Geen handtekening opgeslagen.</div>
            )}
          </div>
        </section>

        <aside className="panel panel-pad detail-panel">
          <div className="detail-section">
            <h2 className="detail-title">Foto's</h2>
            {checkout.photos?.length === 0 && <div className="empty-state">Geen foto's gevonden.</div>}
            <div className="photo-grid">
              {checkout.photos?.map((photo) => (
                <div key={photo.id} className="photo-thumb-wrap">
                  <button type="button" className="checkout-admin-photo" onClick={() => setPreviewPhoto(photo)}>
                    <img className="photo-thumb" src={`${imageBase}${photo.imageUrl}`} alt={photo.label} />
                    <span>{photo.label}</span>
                  </button>
                  <button
                    type="button"
                    className="photo-delete-button"
                    onClick={(event) => removePhoto(event, photo.id)}
                    aria-label="Foto verwijderen"
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Bekende schadegevallen</h2>
            {checkout.existingDamageCases?.length === 0 && <div className="empty-state">Geen schadegevallen gevonden.</div>}
            {checkout.existingDamageCases?.map((damageCase) => (
              <div key={damageCase.id} className="checkout-damage">
                <div className="status-line">
                  <span className="badge danger">{damageCase.status}</span>
                  <span className="badge warning">EUR {damageCase.damageCost || 0}</span>
                </div>
                <p className="page-subtitle">{damageCase.description || "Geen omschrijving"}</p>
              </div>
            ))}
          </div>
        </aside>
      </div>
      {previewPhoto && (
        <button className="image-modal" onClick={() => setPreviewPhoto(null)}>
          <span className="modal-close" aria-hidden="true">Sluiten</span>
          <figure className="checkout-photo-modal" onClick={(event) => event.stopPropagation()}>
            <img src={`${imageBase}${previewPhoto.imageUrl}`} alt={previewPhoto.label} />
            <figcaption>{previewPhoto.label || previewPhoto.type}</figcaption>
          </figure>
        </button>
      )}
    </section>
  );
}
